"use client";

import { useState, useRef } from "react";
import Link from "next/link";
import {
  HiPhotograph,
  HiDownload,
  HiUpload,
  HiRefresh,
  HiQuestionMarkCircle,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import JSZip from "jszip";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

import { useToast } from "@/hooks/use-toast";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";
import { extensionIconResizerFAQ } from "@/lib/data/faq/extension-icon-resizer";

const ICON_SIZES = [16, 32, 48, 128];

export default function ExtensionIconResizerClient() {
  const [sourceImage, setSourceImage] = useState<string | null>(null);
  const [generatedIcons, setGeneratedIcons] = useState<{ size: number; dataUrl: string }[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid file",
        description: "Please upload an image file (PNG, JPG).",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setSourceImage(event.target?.result as string);
      processImage(event.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleFile(file);
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  const processImage = async (src: string) => {
    setIsProcessing(true);
    setGeneratedIcons([]);

    const img = new Image();
    img.src = src;

    await new Promise((resolve) => {
      img.onload = resolve;
    });

    const icons: { size: number; dataUrl: string }[] = [];

    for (const size of ICON_SIZES) {
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // High quality resizing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, 0, 0, size, size);

        icons.push({
          size,
          dataUrl: canvas.toDataURL("image/png"),
        });
      }
    }

    setGeneratedIcons(icons);
    setIsProcessing(false);
  };

  const downloadZip = async () => {
    if (generatedIcons.length === 0) return;

    const zip = new JSZip();

    // Add images to zip
    generatedIcons.forEach((icon) => {
      // Remove data:image/png;base64, prefix
      const base64Data = icon.dataUrl.replace(/^data:image\/png;base64,/, "");
      zip.file(`icon${icon.size}.png`, base64Data, { base64: true });
    });

    // Generate zip
    const content = await zip.generateAsync({ type: "blob" });

    // Download
    const url = URL.createObjectURL(content);
    const a = document.createElement("a");
    a.href = url;
    a.download = "chrome-extension-icons.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast({
      title: "Download started",
      description: "Your icons have been bundled into a ZIP file.",
    });
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-4xl">
      <ToolPageSchema
        name="Chrome Extension Icon Resizer"
        description="Generate all required icon sizes for your Chrome Extension from a single image. Automatically creates 16x16, 32x32, 48x48, and 128x128 icons."
        url="https://plugmonkey.xyz/tool/chrome-extension-icon-resizer/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "Chrome Icon Resizer",
            url: "https://plugmonkey.xyz/tool/chrome-extension-icon-resizer/",
          },
        ]}
        faq={extensionIconResizerFAQ.map((item) => ({
          question: item.question,
          answer: item.answer,
        }))}
      />
      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-purple-600/10 rounded-full text-purple-600">
          <HiPhotograph className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">Chrome Extension Icon Resizer</h1>
          <p className="text-muted-foreground mt-1">
            Generate 16x16, 32x32, 48x48, and 128x128 icons instantly.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Building a Chrome extension? You need icons in multiple sizes—16x16 for the toolbar, 32x32
          for Windows, 48x48 for the extension management page, and 128x128 for the Chrome Web
          Store. Manually resizing images is tedious and error-prone.
        </p>
        <p>
          This tool takes a single high-resolution image and generates all four required sizes
          automatically. Upload your source image (we recommend 512x512px or larger for best
          quality), and we'll create a ZIP file with all the icons ready to drop into your
          extension's manifest. Everything processes in your browser—no uploads, no server
          processing, your files stay local.
        </p>
        <p>
          Perfect for developers who want to ship faster. No design software needed, no manual
          resizing, just upload and download. Your icons are ready in seconds.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HiUpload /> Upload Image
              </CardTitle>
              <CardDescription>
                Upload a high-resolution square image (recommended 512x512px or larger).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-colors cursor-pointer ${
                  isDragging ? "border-primary bg-primary/10" : "border-border hover:bg-muted/50"
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
                aria-label="Upload image file to resize"
              >
                <input
                  id="icon-image"
                  name="image"
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/png, image/jpeg"
                  className="hidden"
                  aria-label="Image file input"
                />

                {sourceImage ? (
                  <div className="flex flex-col items-center">
                    <img
                      src={sourceImage}
                      alt="Uploaded source image for icon generation"
                      className="w-32 h-32 object-contain rounded-lg shadow-sm mb-4"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      <HiRefresh className="mr-2" /> Change Image
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-muted-foreground">
                    <HiUpload className="w-12 h-12 mb-4 opacity-50" />
                    <p className="font-medium">Click to upload or drag and drop</p>
                    <p className="text-sm opacity-70 mt-1">PNG or JPG supported</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {generatedIcons.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Preview</CardTitle>
                <CardDescription>Generated standard Chrome Web Store icon sizes.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-end gap-8 justify-center p-6 bg-muted/20 rounded-lg">
                  {generatedIcons.map((icon) => (
                    <div key={icon.size} className="flex flex-col items-center gap-2">
                      <div className="bg-white rounded-lg shadow-sm p-1">
                        <img
                          src={icon.dataUrl}
                          alt={`${icon.size}px icon`}
                          width={icon.size}
                          height={icon.size}
                        />
                      </div>
                      <span className="text-xs font-mono text-muted-foreground">
                        {icon.size}x{icon.size}
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-6 space-y-6">
            <Card className="bg-muted/50 border-primary/20 shadow-lg">
              <CardHeader>
                <CardTitle>Download</CardTitle>
                <CardDescription>Get all your icons in one ZIP file.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button
                  onClick={downloadZip}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold h-12"
                  disabled={generatedIcons.length === 0 || isProcessing}
                  suppressHydrationWarning
                >
                  <HiDownload className="mr-2 h-5 w-5" />
                  {isProcessing ? "Processing..." : "Download ZIP Bundle"}
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Processed entirely in your browser. No files are uploaded to our server.
                </p>
              </CardContent>
            </Card>

            <div className="bg-card border rounded-xl p-6">
              <h2 className="font-bold text-lg mb-4">Manifest Ready?</h2>
              <p className="text-muted-foreground text-sm mb-4">
                Once you have your icons, you need a <code>manifest.json</code>. Use our generator
                to create one instantly.
              </p>
              <Button asChild variant="secondary" className="w-full">
                <Link href="/tool/manifest-generator/">Go to Manifest Generator</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ
              items={extensionIconResizerFAQ.map((item) => ({
                question: item.question,
                answer: item.answer,
              }))}
              showSupportLink={false}
              className="space-y-4"
            />
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
